import React, { useEffect, useState } from 'react';
import { BedDouble, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { fireHaptic } from '../../hooks/useHaptics';
import { trackEvent } from '../../utils/analytics';

/**
 * ChatBookNowPill — V5.6 · AI-booking handoff.
 *
 * Renders below the ChatHeader when `sessionStorage['travelai:context']`
 * is fresh (< 60 min). Shows the destination + dates the concierge is
 * working with and gives the visitor a one-click "Резервирай сега" pill
 * that opens the Booking.com search with our affiliate label baked in —
 * so every conversion from chat still earns commission.
 *
 * Trust-first: never invents availability or a hotel name. The link
 * points at Booking's real search results for the exact city + dates
 * + guest count carried through from Flights.
 */
const AFFILIATE_LABEL = 'tp-718524-travelai_web_chat-lngen';

const BG_MONTHS = [
  'януари','февруари','март','април','май','юни',
  'юли','август','септември','октомври','ноември','декември',
];

function fmtRange(fromIso, toIso, language) {
  if (!fromIso) return '';
  try {
    const bg   = language === 'bg';
    const a    = new Date(`${fromIso}T00:00:00`);
    const b    = toIso ? new Date(`${toIso}T00:00:00`) : null;
    const mon  = (d) => (bg ? BG_MONTHS[d.getMonth()]
                             : d.toLocaleString('en', { month: 'short' }).toLowerCase());
    if (!b) return `${a.getDate()} ${mon(a)}`;
    if (a.getMonth() === b.getMonth()) return `${a.getDate()}–${b.getDate()} ${mon(a)}`;
    return `${a.getDate()} ${mon(a)} – ${b.getDate()} ${mon(b)}`;
  } catch { return ''; }
}

function buildBookingUrl(ctx) {
  const guests = Math.max(1, Number(ctx.guests) || 2);
  const params = new URLSearchParams({
    ss:            ctx.destination_city,
    checkin:       ctx.checkin || '',
    checkout:      ctx.checkout || '',
    group_adults:  String(guests),
    no_rooms:      '1',
    label:         AFFILIATE_LABEL,
  });
  return `https://www.booking.com/searchresults.html?${params.toString()}`;
}

export default function ChatBookNowPill() {
  const { language } = useLanguage();
  const [ctx, setCtx] = useState(null);

  // Poll sessionStorage on mount + when the chat surface bubbles a
  // context-updated event. Cheap — one JSON parse.
  useEffect(() => {
    const load = () => {
      try {
        const raw = sessionStorage.getItem('travelai:context');
        if (!raw) { setCtx(null); return; }
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.ts || Date.now() - parsed.ts > 60 * 60 * 1000) {
          setCtx(null); return;
        }
        if (!parsed.destination_city || !parsed.checkin || !parsed.checkout) {
          setCtx(null); return;
        }
        setCtx(parsed);
      } catch { setCtx(null); }
    };
    load();
    const onStorage = (e) => { if (!e || e.key === 'travelai:context') load(); };
    const onCustom  = () => load();
    window.addEventListener('storage', onStorage);
    window.addEventListener('travelai:context:updated', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('travelai:context:updated', onCustom);
    };
  }, []);

  if (!ctx) return null;

  const dateLabel = fmtRange(ctx.checkin, ctx.checkout, language);
  const url = buildBookingUrl(ctx);

  return (
    <div
      data-testid="chat-book-now-pill"
      className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3 border-b border-gold-300/40 bg-gradient-to-r from-amber-50 via-amber-50/60 to-white"
    >
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-full bg-midnight-900 text-gold-300 flex items-center justify-center flex-shrink-0">
          <BedDouble className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 leading-tight">
          <div className="text-[9px] uppercase tracking-[0.28em] font-semibold text-gold-700">
            {language === 'bg' ? 'Готово за резервация' : 'Ready to book'}
          </div>
          <div className="text-sm text-midnight-800 truncate">
            <strong className="font-semibold text-midnight-900">{ctx.destination_city}</strong>
            {dateLabel && <> · <span className="text-midnight-600">{dateLabel}</span></>}
            {ctx.guests > 0 && (
              <>
                {' · '}
                <span className="text-midnight-600">
                  {ctx.guests} {language === 'bg'
                    ? (ctx.guests === 1 ? 'гост' : 'гости')
                    : (ctx.guests === 1 ? 'guest' : 'guests')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <a
        href={url}
        target="_blank"
        rel="nofollow sponsored noopener"
        onClick={() => {
          fireHaptic('commit');
          trackEvent('chat_book_now_click', {
            iata:      ctx.destination_iata,
            checkin:   ctx.checkin,
            checkout:  ctx.checkout,
            guests:    ctx.guests || null,
            source:    ctx.source || null,
          });
        }}
        data-testid="chat-book-now-cta"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs sm:text-sm uppercase tracking-[0.18em] font-semibold text-white bg-midnight-900 hover:bg-midnight-800 hover:shadow-md active:translate-y-px transition-all flex-shrink-0 self-start sm:self-auto"
      >
        {language === 'bg' ? 'Резервирай сега' : 'Book now'}
        <ExternalLink className="w-3.5 h-3.5" aria-hidden />
      </a>
    </div>
  );
}
